import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { environment } from '@environments/environment';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { EdTreatmentService } from '@services/ed-treatment.service';
import { FemaleCondomService } from '@services/female-condom.service';
import { MycoplasmaService } from '@services/mycoplasma.service';
import { PartialResultsDeliveryService } from '@services/partial-results-delivery.service';
import { ScheduleOnceService } from '@services/schedule-once.service';
import { StorageService } from '@services/storage.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private router = inject(Router);
  private storage = inject(StorageService);
  private injector = inject(Injector);
  private dynamicFormsService = inject(DynamicFormsService);
  private scheduleOnceService = inject(ScheduleOnceService);
  private consultationStorageService = inject(ConsultationStorageService);
  private femaleCondomService = inject(FemaleCondomService);
  private edTreatmentService = inject(EdTreatmentService);

  /**
   * Handles navigation after a successful consultation request update.
   *
   * @param {string | null} scheduleOnceId the OnceHub ID for scheduling consultations, if applicable
   * @param {string} currentUrl optional current URL for navigation context
   *
   * @returns {Promise<boolean>} promise that resolves to true if navigation is successful, false otherwise
   */
  public handlePostConsultationUpdateNavigation(scheduleOnceId: string | null, currentUrl?: string): Promise<boolean> {
    const treatmentType = this.consultationStorageService.treatmentType;

    if (
      scheduleOnceId &&
      this.consultationStorageService.consultationStatus !== ConsultationStatus.Disqualified &&
      !environment.disableSchedulingRedirectForTreatmentTypes.includes(treatmentType)
    ) {
      const scheduleUrl = this.scheduleOnceService.getScheduleConsultationUrl(
        this.consultationStorageService.consultationId,
        scheduleOnceId,
        this.consultationStorageService.transactionId,
        this.consultationStorageService.hash,
        this.consultationStorageService.patient.phone,
      );

      return this.router.navigateByUrl(scheduleUrl);
    }

    return this.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType, currentUrl);
  }

  /**
   * Gets the next navigation URL based on the current URL.
   *
   * @param {string} currentUrl the current URL (useful in guards/resolvers where navigation hasn't completed yet)
   *
   * @returns {string} the next navigation URL
   */
  public getNextNavigationUrl(currentUrl?: string): string {
    switch ((currentUrl || this.router.url).split('?')[0]) {
      case '/order-address.php':
        return this.getOrderAddressUrl();
      case '/mycoplasma-genitalium':
      case '/mycoplasma-hominis-ureaplasma':
        return this.getBetterLabOrUpsellUrl();
      case '/partial-results-delivery':
        return '/doxy-pep-treatment';
      case '/doxy-pep-treatment':
        return this.getGenderBasedUpsellUrl();
      case '/female-condom':
        return '/order-complete.php';
      case '/ed-treatment':
        return '/order-complete.php';
      default:
        return '';
    }
  }

  /**
   * Gets the URL for the order address.
   */
  public getOrderAddressUrl(): string {
    const mycoplasmaService = this.injector.get<MycoplasmaService>(MycoplasmaService);
    const hasGenitalium = mycoplasmaService.isMycoplasmaGenitaliumTestOrdered();
    const hasHominisWithUreaplasma = mycoplasmaService.isMycoplasmaHominisWithUreaplasmaTestOrdered();

    if (hasGenitalium && hasHominisWithUreaplasma) {
      return this.getBetterLabOrUpsellUrl();
    }

    return hasGenitalium ? mycoplasmaService.mycoplasmaHominisUreaplasmaURL : mycoplasmaService.mycoplasmaGenitaliumURL;
  }

  /**
   * Gets the URL for the better lab or upsell page.
   */
  public getBetterLabOrUpsellUrl(): string {
    const partialResultsDeliveryService =
      this.injector.get<PartialResultsDeliveryService>(PartialResultsDeliveryService);

    if (this.storage.betterLab && !this.storage.free) {
      return 'betterlab.php';
    }

    if (partialResultsDeliveryService.shouldRedirectToPartialResultsDelivery()) {
      return 'partial-results-delivery';
    }

    return 'doxy-pep-treatment';
  }

  /**
   * Gets the gender-based upsell URL for the current order.
   *
   * @returns {string} the upsell URL for the patient's gender
   */
  public getGenderBasedUpsellUrl(): string {
    if (this.edTreatmentService.shouldOfferUpsell()) {
      return '/ed-treatment';
    }

    if (this.femaleCondomService.shouldRedirectToFemaleCondom()) {
      return '/female-condom';
    }

    return '/order-complete.php';
  }

  /**
   * Navigates to the next consultation request page based on the treatment type.
   *
   * @param {ConsultationTreatmentTypes} treatmentType the type of treatment for which to navigate to the next
   * consultation request page
   * @param {string} currentUrl the current URL from which to determine the next page URL
   *
   * @returns {Promise<void>} a promise that resolves when the navigation is complete
   */
  public async navigateToNextConsultationRequestPage(
    treatmentType: ConsultationTreatmentTypes,
    currentUrl?: string,
  ): Promise<boolean> {
    return this.router.navigateByUrl(await this.getConsultationRequestNextPageUrl(treatmentType, currentUrl));
  }

  /**
   * Retrieves the next page URL for a consultation request based on the treatment type.
   *
   * @param {ConsultationTreatmentTypes} treatmentType the type of treatment for which to retrieve the next page URL
   * @param {string} currentUrl the current URL from which to determine the next page URL
   *
   * @returns {Promise<string | undefined>} a promise that resolves to the next page URL or `undefined` if no next page
   * is found
   *
   * @throws {Error} if the current URL is not found in the navigation URLs of the questionnaire
   */
  public getConsultationRequestNextPageUrl(
    treatmentType: ConsultationTreatmentTypes,
    currentUrl?: string,
  ): Promise<string | undefined> {
    return this.dynamicFormsService
      .getQuestionnaire(treatmentType)
      .then((questionnaire) => this.getNextUrl(questionnaire.navigationUrls, currentUrl));
  }

  /**
   * Navigates to the consultation request submission error page.
   *
   * @returns {Promise<boolean>} a promise that resolves to true if navigation is successful, false otherwise
   */
  public navigateToConsultationRequestSubmissionErrorPage(): Promise<boolean> {
    return this.router.navigateByUrl('/consultation/submission-error');
  }

  /**
   * Determines the next URL in the provided list of URLs.
   *
   * @param {string[]} urls the list of URLs
   * @param {string} currentUrl the current URL from which to determine the next URL
   *
   * @returns {string | undefined} the next URL in the list, or `undefined` if no next URL exists
   *
   * @throws {Error} if the current URL is not found in the list of URLs
   */
  private getNextUrl(urls: string[], currentUrl?: string): string | undefined {
    const currentUrlIndex = urls.indexOf(currentUrl || this.router.url.split('?')[0]);
    if (currentUrlIndex === -1) {
      throw new Error('Current URL not found in the provided URLs');
    }

    return urls[currentUrlIndex + 1];
  }
}
